/**
 * Tutorial overlay (DOM): the prompt panel (step, title, one line with keycap chips, a short
 * checklist, Skip tutorial), "New skill" toasts, the orange-telegraph alert, the gold ground
 * marker (projected each frame; an edge arrow when off screen), the pointer on the button / party
 * frames to press, and the dimmed locked binds on the action bar.
 * Styled like the in-run HUD (Aion-like: dark translucent panel, thin gold trim, keycaps).
 */
import type { Vec2 } from '@keystone/sim';
import { iconUrl } from '../hud/icons';
import type { Focus, Lock, PromptView, Toast } from './director';
import './tutorial.css';

const TOAST_MS = 5500;
const MAX_TOASTS = 2;

export interface OverlayFrame {
  view: PromptView | null;
  toasts: Toast[];
  /** Binds still locked (dimmed). */
  locked: Lock[];
  project(p: Vec2, height?: number): { x: number; y: number; visible: boolean };
  /** Shown instead of the prompt once the last step is done. */
  finished: boolean;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls: string, parent?: HTMLElement, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  parent?.appendChild(e);
  return e;
}

/** "Press [1] Shield Slam" → text nodes and keycap chips. */
export function renderKeyText(parent: HTMLElement, text: string): void {
  parent.replaceChildren();
  const parts = text.split(/\[([^\]]+)\]/);
  parts.forEach((p, i) => {
    if (i % 2 === 1) el('span', 'hk-keycap tut-key', parent, p);
    else if (p) parent.appendChild(document.createTextNode(p));
  });
}

export class TutorialOverlay {
  readonly root: HTMLElement;
  private readonly panel: HTMLElement;
  private readonly stepEl: HTMLElement;
  private readonly pips: HTMLElement;
  private readonly titleEl: HTMLElement;
  private readonly textEl: HTMLElement;
  private readonly goalsEl: HTMLElement;
  private readonly alertEl: HTMLElement;
  private readonly toastBox: HTMLElement;
  private readonly marker: HTMLElement;
  private readonly arrow: HTMLElement;
  /** Bouncing chevron over the button / frame to press. */
  private readonly pointer: HTMLElement;
  private lastKey = '';
  private lastAlert = '';
  private lastLocked = '';
  private lockFrames = 0;
  private focusEl: Element | null = null;
  private readonly pending: Toast[] = [];
  private readonly shown: { el: HTMLElement; until: number }[] = [];
  private lastToastAt = 0;

  constructor(
    private readonly host: HTMLElement,
    opts: { onSkip(): void },
  ) {
    this.root = el('div', 'tut-root', host);
    // The tutorial grants nothing: hide the kill-reward pops and the loot feed while it runs.
    host.classList.add('tut-active');
    // Ground marker and edge arrow first: the prompt draws over them. The prompt and the toasts
    // stack in the left column under the party frames (the loot feed's spot, hidden here), out of
    // the way of the enemies in front of you.
    this.marker = el('div', 'tut-marker', this.root);
    el('i', 'tut-marker-ring', this.marker);
    el('i', 'tut-marker-chev', this.marker);
    this.arrow = el('div', 'tut-edge', this.root);
    this.pointer = el('div', 'tut-point', this.root);
    const stack = el('div', 'tut-stack', this.root);
    this.panel = el('section', 'tut-panel', stack);
    const head = el('div', 'tut-head', this.panel);
    el('span', 'tut-badge', head, 'Training');
    this.stepEl = el('span', 'tut-step', head);
    this.pips = el('span', 'tut-pips', head);
    const skip = el('button', 'tut-skip', head, 'Skip tutorial');
    skip.type = 'button';
    skip.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      opts.onSkip();
    });
    this.titleEl = el('div', 'tut-title', this.panel);
    this.textEl = el('div', 'tut-text', this.panel);
    this.goalsEl = el('ul', 'tut-goals', this.panel);
    this.toastBox = el('div', 'tut-toasts', stack);
    this.alertEl = el('div', 'tut-alert', this.root);
  }

  render(f: OverlayFrame, now: number): void {
    const v = f.view;
    // ---- prompt
    const key = v ? `${v.stepNo}|${v.title}|${v.text}|${v.goals.map((g) => `${g.label}${g.done}`).join(',')}` : f.finished ? 'done' : '';
    if (key !== this.lastKey) {
      this.lastKey = key;
      if (v) {
        this.stepEl.textContent = `Step ${v.stepNo} of ${v.stepCount}`;
        this.pips.replaceChildren();
        for (let i = 1; i <= v.stepCount; i++) el('i', i < v.stepNo ? 'done' : i === v.stepNo ? 'on' : '', this.pips);
        this.titleEl.textContent = v.title;
        renderKeyText(this.textEl, v.text);
        this.goalsEl.replaceChildren();
        for (const g of v.goals) {
          const li = el('li', g.done ? 'done' : '', this.goalsEl);
          renderKeyText(li, g.label);
        }
        this.panel.classList.remove('tut-finished');
        // Re-trigger the "new step" sheen.
        this.panel.classList.remove('tut-flash');
        void this.panel.offsetWidth;
        this.panel.classList.add('tut-flash');
      } else if (f.finished) {
        this.stepEl.textContent = 'Complete';
        this.pips.replaceChildren();
        this.titleEl.textContent = 'Training complete!';
        renderKeyText(this.textEl, 'Finish the fight. Your results are coming up.');
        this.goalsEl.replaceChildren();
        this.panel.classList.add('tut-finished');
      }
    }
    this.panel.style.display = v || f.finished ? '' : 'none';

    // ---- alert
    const alert = v?.alert ?? '';
    if (alert !== this.lastAlert) {
      this.lastAlert = alert;
      renderKeyText(this.alertEl, alert);
      this.alertEl.classList.toggle('on', !!alert);
    }

    // ---- focus pointer
    this.setFocus(v?.focus ?? null);
    this.placePointer();

    // ---- locked binds
    const sig = f.locked.join(',');
    if (sig !== this.lastLocked || ++this.lockFrames % 60 === 0) {
      this.lastLocked = sig;
      const locked = new Set(f.locked);
      for (const a of this.host.querySelectorAll<HTMLElement>('.actionbar .action')) {
        const b = [...a.classList].find((c) => c.startsWith('bind-'))?.slice(5);
        a.classList.toggle('tut-locked', !!b && locked.has(b as Lock));
      }
      for (const d of this.host.querySelectorAll<HTMLElement>('.action-dodge')) d.classList.toggle('tut-locked', locked.has('dodge'));
    }

    // ---- marker
    this.placeMarker(v?.marker ?? null, f);

    // ---- toasts
    this.pending.push(...f.toasts);
    for (let i = this.shown.length - 1; i >= 0; i--) {
      const s = this.shown[i]!;
      if (now >= s.until) {
        s.el.classList.add('out');
        const node = s.el;
        setTimeout(() => node.remove(), 400);
        this.shown.splice(i, 1);
      }
    }
    if (this.pending.length && this.shown.length < MAX_TOASTS && now - this.lastToastAt > 600) {
      const t = this.pending.shift()!;
      this.lastToastAt = now;
      this.shown.push({ el: this.toast(t), until: now + TOAST_MS });
    }
  }

  private toast(t: Toast): HTMLElement {
    const card = el('div', 'tut-toast', this.toastBox);
    const icon = el('div', 'tut-toast-icon', card);
    const url = iconUrl(t.unlock.abilityId, 64);
    if (url) icon.style.backgroundImage = `url("${url}")`;
    el('span', 'hk-keycap tut-key tut-toast-key', icon, t.unlock.key);
    const body = el('div', 'tut-toast-body', card);
    el('div', 'tut-toast-label', body, 'New skill');
    el('div', 'tut-toast-name', body, t.unlock.name);
    el('div', 'tut-toast-why', body, t.unlock.why);
    return card;
  }

  private setFocus(focus: Focus): void {
    const target = focus ? this.focusTarget(focus) : null;
    if (target === this.focusEl) return;
    this.focusEl?.classList.remove('tut-focus');
    this.focusEl = target;
    target?.classList.add('tut-focus');
  }

  /** The chevron sits over the focused element (one rect read per frame while something is focused). */
  private placePointer(): void {
    const t = this.focusEl;
    if (!t || !t.isConnected) {
      this.pointer.style.display = 'none';
      return;
    }
    const r = t.getBoundingClientRect();
    if (r.width === 0) {
      this.pointer.style.display = 'none';
      return;
    }
    this.pointer.style.display = '';
    this.pointer.classList.toggle('side', t.classList.contains('hk-r-party'));
    // Party frames: point from the right side; buttons: from above.
    this.pointer.style.transform = t.classList.contains('hk-r-party') ? `translate(${Math.round(r.right + 6)}px, ${Math.round(r.top + Math.min(r.height, 60) / 2)}px)` : `translate(${Math.round(r.left + r.width / 2)}px, ${Math.round(r.top - 6)}px)`;
  }

  private focusTarget(focus: Exclude<Focus, null>): Element | null {
    if (focus === 'party') return this.host.querySelector('.hk-r-party');
    if (focus === 'dodge') return this.host.querySelector('.action-dodge');
    return this.host.querySelector(`.actionbar .action.bind-${focus}`);
  }

  private placeMarker(p: Vec2 | null, f: OverlayFrame): void {
    if (!p) {
      this.marker.style.display = 'none';
      this.arrow.style.display = 'none';
      return;
    }
    const s = f.project(p, 0);
    const W = window.innerWidth;
    const H = window.innerHeight;
    const m = 48;
    if (s.visible && s.x >= m && s.x <= W - m && s.y >= m && s.y <= H - m) {
      this.marker.style.display = '';
      this.arrow.style.display = 'none';
      this.marker.style.transform = `translate(${Math.round(s.x)}px, ${Math.round(s.y)}px)`;
      return;
    }
    // Off screen: an arrow on the screen edge pointing toward it.
    this.marker.style.display = 'none';
    this.arrow.style.display = '';
    const cx = W / 2;
    const cy = H / 2;
    let dx = s.x - cx;
    let dy = s.y - cy;
    if (!s.visible) {
      // Behind the camera the projection mirrors: flip it.
      dx = -dx;
      dy = -dy;
    }
    const k = Math.min((W / 2 - m) / Math.max(1e-3, Math.abs(dx)), (H / 2 - m) / Math.max(1e-3, Math.abs(dy)));
    const ang = Math.atan2(dy, dx);
    this.arrow.style.transform = `translate(${Math.round(cx + dx * k)}px, ${Math.round(cy + dy * k)}px) rotate(${ang}rad)`;
  }

  dispose(): void {
    this.focusEl?.classList.remove('tut-focus');
    for (const a of this.host.querySelectorAll('.tut-locked')) a.classList.remove('tut-locked');
    this.host.classList.remove('tut-active');
    this.root.remove();
  }
}
